import { DetailModalControls } from './DetailModalControls';
import { useEmployeeDetailFilters } from './EmployeeDetailFilters';
import { sumRunRates } from './gaRunRates';
import GARecordDetails from './GARecordDetails';
import { filterRecords, pctLine, pctOfMetric, scopeSummaryRow, scopedMetricValue, variancePct } from './gaReporting';
import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

// Maps the global filter bar's Category value to this page's section keys —
// the section-picker buttons that used to live on this tab are gone, driven
// by that shared dropdown instead so Dashboard and G&A Summary always agree
// on which slice (Total/Headcount/Non-Headcount) is being viewed.
// Category is multi-select (array) now — selecting neither or both still
// means Total (same meaning as the old single-select 'total'); only
// selecting exactly one of Headcount/Non-Headcount narrows the section.
function sectionForCategory(category) {
    if (category.length === 1 && category[0] === 'hc') return 'headcount_costs';
    if (category.length === 1 && category[0] === 'nonhc') return 'non_headcount_costs';
    return 'ga_total';
}
// Merges several rows' own monthly series into one (summing actual/aop/py
// per month, same as several Countries or Statuses summing together) — used
// when more than one Department Sub-Category is selected, so the KPI tiles
// and `total` row reflect their combined total instead of just the first.
function mergeMonthlyRows(rows) {
    if (rows.length <= 1) return rows[0];
    const byMonth = new Map();
    rows.flatMap(r => r.monthly || []).forEach(m => {
        const existing = byMonth.get(m.month);
        byMonth.set(m.month, existing
            ? { ...existing, actual: existing.actual + (m.actual || 0), aop: existing.aop + (m.aop || 0), py: existing.py + (m.py || 0) }
            : { ...m });
    });
    return {
        label: rows.map(r => r.label).join(' + '),
        monthly: Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month)),
        run_rate: sumRunRates(rows.map(r => r.run_rate)),
    };
}
const SECTION_LABEL = { ga_total: 'G&A Total', headcount_costs: 'Headcount Costs', non_headcount_costs: 'Non-Headcount Costs' };

// Every figure is expressed in thousands with a trailing "K" — no M/B
// switch at larger magnitudes — so every card/table on this dashboard uses
// one consistent unit (matches gaReporting.js's money()).
function fmtMoney(value) {
    if (value == null) return 'Unavailable';
    const num = Number(value || 0);
    const sign = num < 0 ? '-' : '';
    const thousands = Math.round(Math.abs(num) / 1000);
    return `${sign}$${thousands.toLocaleString('en-US')}K`;
}

// "2026-08" -> "August 2026" (matches GASummaryDashboard.js's identical helper).
function fmtMonthLabel(iso) {
    if (!iso) return '—';
    const [year, month] = iso.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
const monthAbbrev = iso => fmtMonthLabel(iso).replace(/ \d{4}$/, '');

// Block name that monthly_values is keyed by for a given column group and
// year — matches ga_fiscal_year.py's naming convention on the backend
// (see GASummaryDashboard.js's identical helper for the full note).
function fmtPct(value) {
    if (!Number.isFinite(value)) return null;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
}

// A variance amount plus its percentage against `base`, stacked in one
// cell — same "$ over/under, and by how much %" pairing as the Dashboard
// tab's identical helper (GASummaryDashboard.js), duplicated here per
// this codebase's convention of not sharing state across GA_Elements
// files.
function VarianceStack({ actual, base }) {
    if (actual == null || base == null) return <span>Unavailable</span>;
    const diff = actual - base;
    const pct = base ? fmtPct((diff / Math.abs(base)) * 100) : null;
    const favorable = diff <= 0;
    const color = favorable ? '#15803d' : '#dc2626';
    return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
            <span style={{ color, fontWeight: 700 }}>{fmtMoney(diff)}</span>
            {pct && <span style={{ color, opacity: .72, fontSize: '0.85em', fontWeight: 600 }}>{pct}</span>}
        </span>
    );
}

// Plain "NN.N%" string — used for KpiTile's value prop once a drill modal
// is in percentage mode.
function pctCellValue(value) {
    return value != null ? `${value.toFixed(1)}%` : 'N/A';
}

// Same, but as a table cell — used in place of fmtMoney/VarianceStack once
// a drill modal is in percentage mode (opened from one of the 4 dedicated
// % of Revenue / % of ARR tables), so every figure in it is a share of
// Revenue/ARR instead of a dollar amount.
function pctValueCell(value) {
    return value != null ? <strong>{value.toFixed(1)}%</strong> : <span>—</span>;
}
function pctVarianceCell(value) {
    if (value == null) return <span>—</span>;
    return <strong style={{ color: value <= 0 ? '#15803d' : '#dc2626' }}>{value.toFixed(1)}%</strong>;
}

// Lightweight KPI tile matching the Dashboard tab's esd-kpi-card look —
// built from the same CSS classes rather than importing that component, to
// keep this file's modals visually identical without cross-file coupling.
// `sub`/`subTone` add the "$ and %" second line the Dashboard tab's cards
// carry, so a variance tile here reads the same way.
function KpiTile({ tone, icon, label, value, sub, valueTone, subTone, onClick }) {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag type={onClick ? 'button' : undefined} className={`esd-kpi-card esd-kpi-card--${tone}`} onClick={onClick}>
            <div className="esd-kpi-top">
                <span className="esd-kpi-icon">{icon}</span>
                {onClick && <span className="esd-kpi-hint">View detail →</span>}
            </div>
            <div className="esd-kpi-label">{label}</div>
            <div className={`esd-kpi-value${valueTone ? ` tone-${valueTone}` : ''}`}>{value}</div>
            {sub && <div className={`esd-kpi-sub${subTone ? ` tone-${subTone}` : ''}`}>{sub}</div>}
        </Tag>
    );
}

// First drill layer, opened from a category row (or the Total row) in the
// "By Category" table. Every Actual/AOP/PY cell in Monthly Detail is
// independently clickable: on the single-source sections (Headcount Costs /
// Non-Headcount Costs) it jumps straight to the underlying records for that
// exact month + column; on G&A Total (which combines both sources) it opens
// the HC/Non-HC split first, since there's an extra layer of detail to
// resolve before records. The FY Total row and the Run Rate section follow
// the exact same rule, just summed over the whole year instead of one month.
function CategoryDrillModal({ row, currentYear, priorYear, sectionKey, pctActual, pctAop, pctMetric, pctOfRow, pctOfRunRate, revenueArr, onClose, onDrillMonth, onDrillRecords, onFyTotalClick, onRunRateClick }) {
    const favorableAop = row.fcst_vs_aop_full_year <= 0;
    const favorablePy = row.fcst_vs_py_full_year <= 0;
    const pctVsAop = row.fy_aop ? fmtPct((row.fcst_vs_aop_full_year / Math.abs(row.fy_aop)) * 100) : null;
    const pctVsPy = row.fy_prior_year ? fmtPct((row.fcst_vs_py_full_year / Math.abs(row.fy_prior_year)) * 100) : null;
    const drillHint = sectionKey === 'ga_total' ? 'the HC / Non-HC split' : sectionKey === 'headcount_costs' ? 'the employees behind it' : 'the line items behind it';

    // Opened from one of the 4 dedicated % of Revenue / % of ARR tables —
    // every figure below becomes that category's own share of Revenue/ARR
    // instead of a dollar amount. Reuses the exact same pctOfRow/
    // pctOfRunRate functions (and denominators) the page's own % cards and
    // tables are built from, so these numbers can never drift from what's
    // shown there. Monthly/Quarterly use the matching month's own Revenue/
    // ARR value (not the scoped/FY denominator those two functions use) so
    // a slow-revenue month still shows its own real ratio. Record-level
    // drill-down doesn't apply to a percentage (there's no "record" behind
    // a ratio), so every cell here is a plain, non-interactive value.
    const metricSeries = pctMetric === 'revenue' ? revenueArr?.revenue : pctMetric === 'arr' ? revenueArr?.ending_arr : null;
    const metricMonthly = Object.fromEntries((metricSeries?.monthly || []).map(mm => [mm.month, mm]));
    const metricForMonths = (months, field) => {
        const rows = months.map(m => metricMonthly[m]).filter(Boolean);
        if (!rows.length) return null;
        return metricSeries.is_balance ? (rows[rows.length - 1][field] || 0) : rows.reduce((s, r) => s + (r[field] || 0), 0);
    };
    const pctForMonths = (value, months, field) => {
        const denom = metricForMonths(months, field);
        return denom ? (value / denom) * 100 : null;
    };
    const quarterMonths = q => row.monthly.filter(m => Math.ceil(Number(m.month.slice(5, 7)) / 3) === Number(q.slice(1))).map(m => m.month);

    if (pctMetric) {
        const metricLabel = pctMetric === 'revenue' ? 'Revenue' : 'ARR';
        const pctActualVal = pctOfRow(row, 'fy_actual_forecast')[pctMetric];
        const pctAopVal = pctOfRow(row, 'fy_aop')[pctMetric];
        const pctPyVal = pctOfRow(row, 'fy_prior_year')[pctMetric];
        const pctVarAopVal = pctOfRow(row, 'fcst_vs_aop_full_year')[pctMetric];
        const pctVarPyVal = pctOfRow(row, 'fcst_vs_py_full_year')[pctMetric];

        return (
            <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 940 }}>
                    <div className="legal-modal-hdr">
                        <div className="legal-modal-icon">🔎</div>
                        <div>
                            <div className="legal-modal-title">{row.label}</div>
                            <div className="legal-modal-sub">% of {metricLabel} breakdown — Actual/FCST vs. AOP vs. Prior Year</div>
                        </div>
                        <div className="legal-modal-hdr-actions">
                            <button className="legal-modal-close" onClick={onClose} title="Close">✕</button>
                        </div>
                    </div>
                    <div className="legal-modal-body">
                        <div className="esd-kpi-grid" style={{ marginBottom: 20 }}>
                            <KpiTile tone="b" icon="💹" label={`Actual/Forecast % of ${metricLabel}`} value={pctCellValue(pctActualVal)} />
                            <KpiTile tone="b" icon="💹" label={`AOP % of ${metricLabel}`} value={pctCellValue(pctAopVal)} />
                            <KpiTile tone="b" icon="💹" label={`Prior Year % of ${metricLabel}`} value={pctCellValue(pctPyVal)} />
                            <KpiTile
                                tone={pctVarAopVal <= 0 ? 'good' : 'bad'} icon={pctVarAopVal <= 0 ? '✅' : '⚠️'} label={`vs. AOP % of ${metricLabel}`}
                                value={pctCellValue(pctVarAopVal)} valueTone={pctVarAopVal <= 0 ? 'good' : 'bad'}
                            />
                            <KpiTile
                                tone={pctVarPyVal <= 0 ? 'good' : 'bad'} icon={pctVarPyVal <= 0 ? '✅' : '⚠️'} label={`vs. PY % of ${metricLabel}`}
                                value={pctCellValue(pctVarPyVal)} valueTone={pctVarPyVal <= 0 ? 'good' : 'bad'}
                            />
                        </div>

                        <div className="esd-section">
                            <div className="esd-section-head">
                                <span className="esd-section-icon">🗓️</span>
                                <span className="esd-section-title">Monthly Detail — % of {metricLabel}</span>
                            </div>
                            <div className="esd-table-wrap">
                                <table className="esd-table">
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left' }}>Month</th>
                                            <th>{currentYear} Actual/FCST</th>
                                            <th>{currentYear} AOP</th>
                                            <th>{priorYear} PY</th>
                                            <th>Var. to AOP</th>
                                            <th>Var. to PY</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {row.monthly.map(m => (
                                            <tr key={m.month}>
                                                <td style={{ textAlign: 'left' }}>{m.label}</td>
                                                <td>{pctValueCell(pctForMonths(m.actual, [m.month], 'actual'))}</td>
                                                <td>{pctValueCell(pctForMonths(m.aop, [m.month], 'aop'))}</td>
                                                <td>{pctValueCell(pctForMonths(m.py, [m.month], 'py'))}</td>
                                                <td>{pctVarianceCell(pctForMonths(m.actual - m.aop, [m.month], 'actual'))}</td>
                                                <td>{pctVarianceCell(pctForMonths(m.actual - m.py, [m.month], 'actual'))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td style={{ textAlign: 'left' }}><strong>Period Total</strong></td>
                                            <td>{pctValueCell(pctActualVal)}</td>
                                            <td>{pctValueCell(pctAopVal)}</td>
                                            <td>{pctValueCell(pctPyVal)}</td>
                                            <td>{pctVarianceCell(pctVarAopVal)}</td>
                                            <td>{pctVarianceCell(pctVarPyVal)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className="esd-section">
                            <div className="esd-section-head">
                                <span className="esd-section-icon">📊</span>
                                <span className="esd-section-title">Quarterly — % of {metricLabel}</span>
                            </div>
                            <div className="esd-table-wrap">
                                <table className="esd-table esd-table-static">
                                    <thead><tr><th style={{ textAlign: 'left' }}></th><th>Q1</th><th>Q2</th><th>Q3</th><th>Q4</th></tr></thead>
                                    <tbody>
                                        <tr><td style={{ textAlign: 'left' }}>Actual/FCST</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{pctValueCell(pctForMonths(row.quarterly[q].actual, quarterMonths(q), 'actual'))}</td>)}</tr>
                                        <tr><td style={{ textAlign: 'left' }}>AOP</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{pctValueCell(pctForMonths(row.quarterly[q].aop, quarterMonths(q), 'aop'))}</td>)}</tr>
                                        <tr><td style={{ textAlign: 'left' }}>PY</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{pctValueCell(pctForMonths(row.quarterly[q].py, quarterMonths(q), 'py'))}</td>)}</tr>
                                        <tr><td style={{ textAlign: 'left' }}>Var. to AOP</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{pctVarianceCell(pctForMonths(row.quarterly[q].actual - row.quarterly[q].aop, quarterMonths(q), 'actual'))}</td>)}</tr>
                                        <tr><td style={{ textAlign: 'left' }}>Var. to PY</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{pctVarianceCell(pctForMonths(row.quarterly[q].actual - row.quarterly[q].py, quarterMonths(q), 'actual'))}</td>)}</tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="esd-section">
                            <div className="esd-section-head">
                                <span className="esd-section-icon">🔁</span>
                                <span className="esd-section-title">Run Rate — % of {metricLabel}</span>
                            </div>
                            <div className="esd-table-wrap">
                                <table className="esd-table">
                                    <tbody>
                                        <tr>
                                            <td style={{ textAlign: 'left' }}>Current Run Rate</td>
                                            <td>{pctValueCell(pctOfRunRate(row.run_rate, 'current')[pctMetric])}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'left' }}>Dec AOP Run Rate</td>
                                            <td>{pctValueCell(pctOfRunRate(row.run_rate, 'dec_aop')[pctMetric])}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'left' }}>Dec FCST Run Rate</td>
                                            <td>{pctValueCell(pctOfRunRate(row.run_rate, 'dec_fcst')[pctMetric])}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 940 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🔎</div>
                    <div>
                        <div className="legal-modal-title">{row.label}</div>
                        <div className="legal-modal-sub">Monthly breakdown — Actual/FCST vs. AOP vs. Prior Year</div>
                    </div>
                    <div className="legal-modal-hdr-actions">
                        <button className="legal-modal-close" onClick={onClose} title="Close">✕</button>
                    </div>
                </div>
                <div className="legal-modal-body">
                    <div className="esd-kpi-grid" style={{ marginBottom: 20 }}>
                        <KpiTile
                            tone="a" icon="💵" label="Actual/Forecast" value={fmtMoney(row.fy_actual_forecast)}
                            sub={pctLine(pctActual)}
                            onClick={() => onFyTotalClick('actual')}
                        />
                        <KpiTile
                            tone="b" icon="🎯" label="AOP" value={fmtMoney(row.fy_aop)}
                            sub={pctLine(pctAop)}
                            onClick={() => onFyTotalClick('aop')}
                        />
                        <KpiTile
                            tone="c" icon="📅" label="Prior Year" value={fmtMoney(row.fy_prior_year)}
                            onClick={() => onFyTotalClick('py')}
                        />
                        <KpiTile
                            tone={favorableAop ? 'good' : 'bad'} icon={favorableAop ? '✅' : '⚠️'} label="vs. AOP"
                            value={fmtMoney(row.fcst_vs_aop_full_year)} valueTone={favorableAop ? 'good' : 'bad'}
                            sub={pctVsAop} subTone={favorableAop ? 'good' : 'bad'}
                            onClick={() => onFyTotalClick('var_aop')}
                        />
                        <KpiTile
                            tone={favorablePy ? 'good' : 'bad'} icon={favorablePy ? '✅' : '⚠️'} label="vs. PY"
                            value={fmtMoney(row.fcst_vs_py_full_year)} valueTone={favorablePy ? 'good' : 'bad'}
                            sub={pctVsPy} subTone={favorablePy ? 'good' : 'bad'}
                            onClick={() => onFyTotalClick('var_py')}
                        />
                    </div>

                    <div className="esd-section">
                        <div className="esd-section-head">
                            <span className="esd-section-icon">🗓️</span>
                            <span className="esd-section-title">Monthly Detail</span>
                            <span className="esd-section-note">Click a cell for {drillHint}</span>
                        </div>
                        <div className="esd-table-wrap">
                            <table className="esd-table esd-table-cellclick">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Month</th>
                                        <th>{currentYear} Actual/FCST</th>
                                        <th>{currentYear} AOP</th>
                                        <th>{priorYear} PY</th>
                                        <th>Var. to AOP</th>
                                        <th>Var. to PY</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {row.monthly.map(m => {
                                        const go = kind => (sectionKey === 'ga_total' ? onDrillMonth(m.month, m.label) : onDrillRecords(kind, [m.month], m.label));
                                        return (
                                            <tr key={m.month}>
                                                <td style={{ textAlign: 'left' }}>{m.label}</td>
                                                <td className="esd-cell-click" onClick={() => go('actual')} title={`${drillHint} — ${m.label}`}>{fmtMoney(m.actual)}</td>
                                                <td className="esd-cell-click" onClick={() => go('aop')} title={`${drillHint} — ${m.label}`}>{fmtMoney(m.aop)}</td>
                                                <td className="esd-cell-click" onClick={() => go('py')} title={`${drillHint} — ${m.label}`}>{fmtMoney(m.py)}</td>
                                                <td className="esd-cell-click" onClick={() => go('var_aop')} title={`Variance to AOP — ${drillHint} — ${m.label}`}>
                                                    <VarianceStack actual={m.actual} base={m.aop} />
                                                </td>
                                                <td className="esd-cell-click" onClick={() => go('var_py')} title={`Variance to Prior Year — ${drillHint} — ${m.label}`}>
                                                    <VarianceStack actual={m.actual} base={m.py} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}><strong>Period Total</strong></td>
                                        <td className="esd-cell-click" onClick={() => onFyTotalClick('actual')} title={`Period Total — ${drillHint}`}>
                                            <strong>{fmtMoney(row.fy_actual_forecast)}</strong>
                                        </td>
                                        <td className="esd-cell-click" onClick={() => onFyTotalClick('aop')} title={`Period Total — ${drillHint}`}>
                                            <strong>{fmtMoney(row.fy_aop)}</strong>
                                        </td>
                                        <td className="esd-cell-click" onClick={() => onFyTotalClick('py')} title={`Period Total — ${drillHint}`}>
                                            <strong>{fmtMoney(row.fy_prior_year)}</strong>
                                        </td>
                                        <td className="esd-cell-click" onClick={() => onFyTotalClick('var_aop')} title={`Period Total Variance to AOP — ${drillHint}`}>
                                            <VarianceStack actual={row.fy_actual_forecast} base={row.fy_aop} />
                                        </td>
                                        <td className="esd-cell-click" onClick={() => onFyTotalClick('var_py')} title={`Period Total Variance to Prior Year — ${drillHint}`}>
                                            <VarianceStack actual={row.fy_actual_forecast} base={row.fy_prior_year} />
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    <div className="esd-section">
                        <div className="esd-section-head">
                            <span className="esd-section-icon">📊</span>
                            <span className="esd-section-title">Quarterly</span>
                        </div>
                        <div className="esd-table-wrap">
                            <table className="esd-table esd-table-static">
                                <thead><tr><th style={{ textAlign: 'left' }}></th><th>Q1</th><th>Q2</th><th>Q3</th><th>Q4</th></tr></thead>
                                <tbody>
                                    <tr><td style={{ textAlign: 'left' }}>Actual/FCST</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{fmtMoney(row.quarterly[q].actual)}</td>)}</tr>
                                    <tr><td style={{ textAlign: 'left' }}>AOP</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{fmtMoney(row.quarterly[q].aop)}</td>)}</tr>
                                    <tr><td style={{ textAlign: 'left' }}>PY</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}>{fmtMoney(row.quarterly[q].py)}</td>)}</tr>
                                    <tr><td style={{ textAlign: 'left' }}>Var. to AOP</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}><VarianceStack actual={row.quarterly[q].actual} base={row.quarterly[q].aop} /></td>)}</tr>
                                    <tr><td style={{ textAlign: 'left' }}>Var. to PY</td>{['Q1', 'Q2', 'Q3', 'Q4'].map(q => <td key={q}><VarianceStack actual={row.quarterly[q].actual} base={row.quarterly[q].py} /></td>)}</tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="esd-section">
                        <div className="esd-section-head">
                            <span className="esd-section-icon">🔁</span>
                            <span className="esd-section-title">Run Rate</span>
                            <span className="esd-section-note">Click Run Rate for {drillHint}</span>
                        </div>
                        <div className="esd-table-wrap">
                            <table className="esd-table esd-table-cellclick">
                                <tbody>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Current Run Rate</td>
                                        <td className="esd-cell-click" onClick={onRunRateClick} title={`Run Rate — ${drillHint}`}>{fmtMoney(row.run_rate.current)} <span style={{ opacity: .6 }}>📈</span></td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Dec AOP Run Rate</td>
                                        <td className="esd-cell-click" onClick={onRunRateClick} title={`Run Rate — ${drillHint}`}>{fmtMoney(row.run_rate.dec_aop)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}>Dec FCST Run Rate</td>
                                        <td className="esd-cell-click" onClick={onRunRateClick} title={`Run Rate — ${drillHint}`}>{fmtMoney(row.run_rate.dec_fcst)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Second drill layer, reached from a G&A Total category's Monthly Detail (a
// single month) or its FY Total row (the whole year) — G&A Total combines
// Headcount + Non-Headcount, so this is the one place a further split
// actually reveals new data. Shows the full Common Points set (Act, AOP,
// PY, Var. to AOP, Var. to PY) for each source at once, same as the
// Dashboard tab's equivalent split — every cell still drills to its own
// records.
function MonthSplitModal({ title, sub, hc, nonhc, onClose, onBack, onDrillCell }) {
    // A source with nothing in Actual/AOP/PY for this month/scope (e.g. the
    // Category filter narrowed to the other source) is dropped — just
    // noise, same as every other category/department table across the app.
    const rows = [
        { source: 'hc', label: 'Headcount', subLabel: 'At employee level', ...hc },
        { source: 'nonhc', label: 'Non-Headcount', subLabel: 'At vendor / G&A category level', ...nonhc },
    ].filter(r => r.actual || r.aop || r.py);
    const total = rows.reduce((acc, r) => ({
        actual: acc.actual + r.actual, aop: acc.aop + r.aop, py: acc.py + r.py,
    }), { actual: 0, aop: 0, py: 0 });

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 720 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🔎</div>
                    <div>
                        <div className="legal-modal-title">{title}</div>
                        <div className="legal-modal-sub">{sub}</div>
                    </div>
                    <DetailModalControls onBack={onBack} onClose={onClose} />
                </div>
                <div className="legal-modal-body">
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Source</th><th>Actual/Forecast</th><th>AOP</th><th>Prior Year</th>
                                    <th>Var AOP and %</th><th>Var PY and %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.label}>
                                        <td style={{ textAlign: 'left' }}>
                                            <div>{r.label}</div>
                                            <div style={{ fontSize: '0.82em', fontWeight: 500, color: '#65676b' }}>{r.subLabel}</div>
                                        </td>
                                        <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'actual')}><strong>{fmtMoney(r.actual)}</strong></td>
                                        <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'aop')}><strong>{fmtMoney(r.aop)}</strong></td>
                                        <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'py')}><strong>{fmtMoney(r.py)}</strong></td>
                                        <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'var_aop')}>
                                            <VarianceStack actual={r.actual} base={r.aop} />
                                        </td>
                                        <td className="esd-cell-click" onClick={() => onDrillCell(r.source, 'var_py')}>
                                            <VarianceStack actual={r.actual} base={r.py} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="esd-cell-click" onClick={() => onDrillCell('total', 'actual')}>
                                    <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                    <td><strong>{fmtMoney(total.actual)}</strong></td>
                                    <td><strong>{fmtMoney(total.aop)}</strong></td>
                                    <td><strong>{fmtMoney(total.py)}</strong></td>
                                    <td><VarianceStack actual={total.actual} base={total.aop} /></td>
                                    <td><VarianceStack actual={total.actual} base={total.py} /></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Run Rate's own split layer — Current / Dec AOP / Dec FCST Run Rate for
// each source, read straight off the backend's own per-category run_rate
// (ga_summary.py's _build_row already computes it per Headcount/Non-HC
// section, so no client-side month math is needed here). Clicking Headcount
// goes to employee-level records; clicking Non-Headcount goes to vendor
// records — same one-hop depth, matching the Dashboard tab's Run Rate split.
function RunRateSplitModal({ label, hc, nonhc, pctOfRunRate, pctMetric, onClose, onBack, onDrillSource }) {
    // A source with nothing in Current/Dec AOP/Dec FCST Run Rate for this
    // scope is dropped — just noise, same as every other category/department
    // table across the app.
    const rows = [
        { source: 'hc', title: 'Headcount', subLabel: 'At employee level', ...hc },
        { source: 'nonhc', title: 'Non-Headcount', subLabel: 'At vendor / G&A category level', ...nonhc },
    ].filter(r => r.current || r.dec_aop || r.dec_fcst);
    const total = sumRunRates([hc, nonhc]);

    // pctMetric set (opened from a dedicated % table): replace every dollar
    // column with that same metric's percentage, instead of appending extra
    // % columns alongside the dollars (pctOfRunRate's other, additive mode).
    if (pctMetric) {
        const metricLabel = pctMetric === 'revenue' ? '% of Revenue' : '% of ARR';
        const pct = (row, field) => pctOfRunRate(row, field)[pctMetric];
        const cell = v => v != null ? `${v.toFixed(1)}%` : '—';
        return (
            <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 940 }}>
                    <div className="legal-modal-hdr">
                        <div className="legal-modal-icon">🔁</div>
                        <div>
                            <div className="legal-modal-title">{label}</div>
                            <div className="legal-modal-sub">
                                Run Rate — Headcount vs. Non-Headcount · {metricLabel} = Run Rate ÷ {pctMetric === 'revenue' ? 'Revenue' : 'Ending ARR'} × 100
                            </div>
                        </div>
                        <DetailModalControls onBack={onBack} onClose={onClose} />
                    </div>
                    <div className="legal-modal-body">
                        <div className="esd-table-wrap">
                            <table className="esd-table esd-table-cellclick">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Source</th>
                                        <th>Current {metricLabel}</th>
                                        <th>Dec AOP {metricLabel}</th>
                                        <th>Dec FCST {metricLabel}</th>
                                        <th>Variance {metricLabel}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => (
                                        <tr key={r.source} onClick={() => onDrillSource(r.source)}>
                                            <td style={{ textAlign: 'left' }}>
                                                <div>{r.title}</div>
                                                <div style={{ fontSize: '0.82em', fontWeight: 500, color: '#65676b' }}>{r.subLabel}</div>
                                            </td>
                                            <td><strong>{cell(pct(r, 'current'))}</strong></td>
                                            <td><strong>{cell(pct(r, 'dec_aop'))}</strong></td>
                                            <td><strong>{cell(pct(r, 'dec_fcst'))}</strong></td>
                                            <td>{pctVarianceCell(pct(r, 'variance'))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                        <td><strong>{cell(pct(total, 'current'))}</strong></td>
                                        <td><strong>{cell(pct(total, 'dec_aop'))}</strong></td>
                                        <td><strong>{cell(pct(total, 'dec_fcst'))}</strong></td>
                                        <td>{pctVarianceCell(pct(total, 'variance'))}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 940 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🔁</div>
                    <div>
                        <div className="legal-modal-title">{label}</div>
                        <div className="legal-modal-sub">
                            Run Rate — Headcount vs. Non-Headcount
                            {pctOfRunRate && ' · % of Revenue = Current Run Rate ÷ Revenue × 100 · % of ARR = Current Run Rate ÷ Ending ARR × 100'}
                        </div>
                    </div>
                    <DetailModalControls onBack={onBack} onClose={onClose} />
                </div>
                <div className="legal-modal-body">
                    <div className="esd-table-wrap">
                        <table className="esd-table esd-table-cellclick">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Source</th>
                                    <th>Current Run Rate</th>
                                    <th>Dec AOP Run Rate</th>
                                    <th>Dec FCST Run Rate</th>
                                    <th>Var to AOP RR and %</th>
                                    {pctOfRunRate && <th>% of Revenue</th>}
                                    {pctOfRunRate && <th>% of ARR</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.source} onClick={() => onDrillSource(r.source)}>
                                        <td style={{ textAlign: 'left' }}>
                                            <div>{r.title}</div>
                                            <div style={{ fontSize: '0.82em', fontWeight: 500, color: '#65676b' }}>{r.subLabel}</div>
                                        </td>
                                        <td><strong>{fmtMoney(r.current)}</strong></td>
                                        <td><strong>{fmtMoney(r.dec_aop)}</strong></td>
                                        <td><strong>{fmtMoney(r.dec_fcst)}</strong></td>
                                        <td><VarianceStack actual={r.dec_fcst} base={r.dec_aop} /></td>
                                        {pctOfRunRate && <td>{pctOfRunRate(r).revenue != null ? `${pctOfRunRate(r).revenue.toFixed(1)}%` : '—'}</td>}
                                        {pctOfRunRate && <td>{pctOfRunRate(r).arr != null ? `${pctOfRunRate(r).arr.toFixed(1)}%` : '—'}</td>}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                    <td><strong>{fmtMoney(total.current)}</strong></td>
                                    <td><strong>{fmtMoney(total.dec_aop)}</strong></td>
                                    <td><strong>{fmtMoney(total.dec_fcst)}</strong></td>
                                    <td><VarianceStack actual={total.dec_fcst} base={total.dec_aop} /></td>
                                    {pctOfRunRate && <td>{pctOfRunRate(total).revenue != null ? `${pctOfRunRate(total).revenue.toFixed(1)}%` : '—'}</td>}
                                    {pctOfRunRate && <td>{pctOfRunRate(total).arr != null ? `${pctOfRunRate(total).arr.toFixed(1)}%` : '—'}</td>}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Deepest Run Rate layer — every employee (HC) or vendor/line-item (Non-HC)
// behind the source that was clicked, each annualized the same way as the
// summary above it (last actual month × 12, December AOP × 12, December
// forecast × 12).
function RunRateRecordsModal({ source, category, lastActualMonth, decMonth, currentYear, records, loading, error, onClose, onBack }) {
    const isHc = source === 'hc';
    const scoped = (records || []).filter(r => !category || category === 'Total' || (r.category || 'Unspecified') === category);
    const employeeFilters = useEmployeeDetailFilters(scoped, source);
    const valued = employeeFilters.records.map(r => ({
        ...r,
        _current: r.run_rate?.current,
        _decAop: r.run_rate?.dec_aop,
        _decFcst: r.run_rate?.dec_fcst,
        _name: isHc ? (r.headcount_name_actual || r.headcount_name_aop || '') : (r.name || ''),
        _sub: isHc ? (r.status || '') : (r.sub_category || ''),
    }));
    const sorted = [...valued].sort((a, b) => b._current - a._current);
    const totals = sumRunRates(valued.map(r => r.run_rate));

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 900 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">{isHc ? '👥' : '📦'}</div>
                    <div>
                        <div className="legal-modal-title">{isHc ? 'Employees' : 'Non-HC Items'} — {category}</div>
                        <div className="legal-modal-sub">Run Rate by record - OTE, full-year amounts and configured three-month averages</div>
                    </div>
                    <DetailModalControls onBack={onBack} onClose={onClose} />
                </div>
                <div className="legal-modal-body">
                    {!loading && !error && employeeFilters.controls}
                    {error && <p style={{ padding: '8px 0', color: '#dc2626' }}>{error}</p>}
                    {!error && (
                        <div className="esd-table-wrap">
                            <table className="esd-table esd-table-static">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>{isHc ? 'Employee' : 'Name'}</th>
                                        <th style={{ textAlign: 'left' }}>{isHc ? 'Status' : 'Sub-Category'}</th>
                                        <th>Current Run Rate</th>
                                        <th>Dec AOP Run Rate</th>
                                        <th>Dec FCST Run Rate</th>
                                        <th>Var to AOP RR and %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && <tr><td colSpan={6} className="esd-empty-row">Loading…</td></tr>}
                                    {!loading && sorted.map(r => (
                                        <tr key={r.id}>
                                            <td style={{ textAlign: 'left' }}>{r._name || '—'}</td>
                                            <td style={{ textAlign: 'left' }}>{r._sub || '—'}</td>
                                            <td>{fmtMoney(r._current)}</td>
                                            <td>{fmtMoney(r._decAop)}</td>
                                            <td>{fmtMoney(r._decFcst)}</td>
                                            <td><VarianceStack actual={r._decFcst} base={r._decAop} /></td>
                                        </tr>
                                    ))}
                                    {!loading && !sorted.length && (
                                        <tr><td colSpan={6} className="esd-empty-row">No records in this scope.</td></tr>
                                    )}
                                </tbody>
                                {!loading && !!sorted.length && (
                                    <tfoot>
                                        <tr>
                                            <td colSpan={2} style={{ textAlign: 'left' }}><strong>Total</strong></td>
                                            <td><strong>{fmtMoney(totals.current)}</strong></td>
                                            <td><strong>{fmtMoney(totals.dec_aop)}</strong></td>
                                            <td><strong>{fmtMoney(totals.dec_fcst)}</strong></td>
                                            <td><VarianceStack actual={totals.dec_fcst} base={totals.dec_aop} /></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Deepest drill layer — the individual employee (HC) or line-item (Non-HC)
// records behind one cell (a single month, or the whole year for an FY
// Total drill). "Planned" and "actual" HC names are almost always
// identical (a placeholder gets its real hire's name once filled), so the
// actual name leads and a note only appears where they differ.
function RecordsListModal(props) {
    return <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) props.onClose(); }}>
        <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 1100 }}>
            <div className="legal-modal-hdr"><div><div className="legal-modal-title">{props.source === 'hc' ? 'Employees' : props.source === 'total' ? 'Employees and Vendors' : 'G&A Categories / Vendors'}</div><div className="legal-modal-sub">{props.monthLabel}</div></div>
                <DetailModalControls onBack={props.onBack} onClose={props.onClose} />
            </div><div className="legal-modal-body"><GARecordDetails {...props} /></div>
        </div>
    </div>;
}

// Small popup opened by clicking any cell in the By Category / Run Rate by
// Category % of Revenue/ARR tables below — shows just that one percentage
// plus the exact division used to compute it, same component (and same
// convention: a formula text line, plus a "= $X ÷ $Y × 100 = value" line
// when the raw numerator/denominator are known) as GASummaryDashboard.js's
// own PctFormulaModal on the Dashboard tab's % cards.
function PctFormulaModal({ label, value, formula, numerator, denominator, onClose }) {
    const hasValues = numerator != null && denominator;
    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 440 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🧮</div>
                    <div>
                        <div className="legal-modal-title">{label}</div>
                        <div className="legal-modal-sub">Formula</div>
                    </div>
                    <DetailModalControls onClose={onClose} />
                </div>
                <div className="legal-modal-body">
                    <div className="esd-kpi-grid" style={{ marginBottom: 0 }}>
                        <div className="esd-kpi-card esd-kpi-card--b">
                            <div className="esd-kpi-label">{label}</div>
                            <div className="esd-kpi-value">{value}</div>
                            <div className="esd-kpi-sub">Formula: {formula}</div>
                            {hasValues && <div className="esd-kpi-sub">= {fmtMoney(numerator)} ÷ {fmtMoney(denominator)} × 100 = {value}</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Row/Total click on the By Category / Run Rate by Category % of
// Revenue/ARR tables below opens this first — a card per basis (Actuals,
// AOP, PY, Var. to AOP, or their Run Rate equivalents) for that one row,
// each opening PctFormulaModal above for its exact formula — same two-layer
// row-click-then-card-click flow as GlobalSummary.js's own "By Department"
// breakdown, instead of jumping straight to the formula popup.
function PctRowDrillModal({ title, sub, cards, onClose }) {
    const [formulaDrill, setFormulaDrill] = useState(null);
    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal esd esd-compact-modal" style={{ maxWidth: 720 }}>
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">📊</div>
                    <div>
                        <div className="legal-modal-title">{title}</div>
                        <div className="legal-modal-sub">{sub}</div>
                    </div>
                    <DetailModalControls onClose={onClose} />
                </div>
                <div className="legal-modal-body">
                    <div className="esd-kpi-grid" style={{ marginBottom: 0 }}>
                        {cards.map(c => (
                            <button
                                type="button" key={c.label}
                                className="esd-kpi-card esd-kpi-card--b"
                                onClick={() => setFormulaDrill(c)}
                            >
                                <div className="esd-kpi-top"><span className="esd-kpi-hint">View formula →</span></div>
                                <div className="esd-kpi-label">{c.label}</div>
                                <div className="esd-kpi-value">{c.value}</div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {formulaDrill && <PctFormulaModal {...formulaDrill} onClose={() => setFormulaDrill(null)} />}
        </div>
    );
}

function GASummary({
    department = [], quarter = [], customRange = null, category = [], yearMode = 'current',
    hcType = [], status = [], country = [], subCategory = [], vendorName = [], period = [],
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const section = sectionForCategory(category);
    // Stack of drill layers, but only the top is ever mounted — stacking
    // multiple full-screen modals at once lets a smaller/deeper one fail to
    // fully cover the larger one behind it, so both would show at the same
    // time. See GASummaryDashboard.js's DeepDiveModal for the same fix.
    const [drill, setDrill] = useState(null);
    // Independent of the `drill` stack above — a leaf-level popup opened
    // directly from a % table cell, same "not part of the breadcrumb"
    // convention GASummaryDashboard.js's own formulaDrill uses.
    const [pctFormulaDrill, setPctFormulaDrill] = useState(null);
    // Row-level breakdown (layer 1) opened by a row/Total click on the By
    // Category / Run Rate by Category % tables — its own cards each open
    // pctFormulaDrill above (layer 2) for the exact formula.
    const [pctRowDrill, setPctRowDrill] = useState(null);
    const [hcRecords, setHcRecords] = useState(null);
    const [hcLoading, setHcLoading] = useState(false);
    const [hcError, setHcError] = useState('');
    const [nonhcRecords, setNonhcRecords] = useState(null);
    const [nonhcLoading, setNonhcLoading] = useState(false);
    const [nonhcError, setNonhcError] = useState('');

    const filteredHc = filterRecords(hcRecords, 'hc', { hcType, status, country });
    const filteredNonhc = filterRecords(nonhcRecords, 'nonhc', { hcType, status, country, subCategory, vendorName });
    const recordsFor = source => source === 'total'
        ? [...filteredHc.map(r => ({ ...r, _source: 'hc' })), ...filteredNonhc.map(r => ({ ...r, _source: 'nonhc' }))]
        : source === 'hc' ? filteredHc : filteredNonhc;
    useEffect(() => { setDrill(null); }, [department, quarter, customRange, yearMode, hcType, status, country, subCategory, vendorName, period]);

    // Status/HC Type/Country/Sub-Category/Vendor Name are applied
    // server-side — see ga_summary.py's comment — so any of them changing
    // means a re-fetch.
    const query = useMemo(() => {
        const params = new URLSearchParams();
        if (hcType.length) params.set('hc_type', hcType.join(','));
        if (status.length) params.set('status', status.join(','));
        if (country.length) params.set('country', country.join(','));
        if (subCategory.length) params.set('sub_category', subCategory.join(','));
        if (vendorName.length) params.set('name', vendorName.join(','));
        return params.toString();
    }, [hcType, status, country, subCategory, vendorName]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        (async () => {
            try {
                const res = await fetch(`${API_URL}/api/ga/ga-summary/${query ? `?${query}` : ''}`, { credentials: 'include' });
                const body = await readApiJson(res, 'Could not load the G&A summary.');
                if (!cancelled) setData(body);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [query]);

    const sectionData = data?.[section];
    const allCategoryRows = useMemo(() => {
        if (!sectionData) return [];
        return (data.categories || []).map(cat => sectionData.by_category[cat]).filter(Boolean);
    }, [sectionData, data]);
    const departmentFilteredRows = useMemo(
        () => department.length ? allCategoryRows.filter(row => department.includes(row.label)) : allCategoryRows,
        [allCategoryRows, department]
    );

    if (loading) return <div className="pl-empty"><p>Loading G&amp;A summary…</p></div>;
    if (error) return <div className="pl-empty"><p>{error}</p></div>;
    if (!data?.has_data) return <div className="pl-empty"><p>{data?.message || 'No data available yet — upload the Headcount Master and/or Non-HC workbooks.'}</p></div>;

    const rawCurrentYear = data.current_year || '';
    const rawPriorYear = data.prior_year || '';
    // Viewing the prior year swaps which figures count as "current" — its
    // own actuals stand in for Actual/FCST, and there's no AOP or further
    // prior-year data captured anywhere for it, so those come back blank
    // rather than fabricated. Quarter/Custom Date still narrow which months
    // of whichever year is active get summed.
    // Fiscal Year is multi-select — Current + Prior together is treated like
    // Current alone for these chart/label purposes, same reasoning as
    // GASummaryDashboard.js (scopeRow below already sums both years' real
    // numbers via scopeSummaryRow).
    const isPriorOnlyView = yearMode.includes('prior') && !yearMode.includes('current');
    const currentYear = isPriorOnlyView ? rawPriorYear : rawCurrentYear;
    const priorYear = isPriorOnlyView ? '' : rawPriorYear;
    // Same "YTD-Aug" / "Q1" / "Jan–Apr" / "FY26" convention as the Dashboard
    // tab's periodTag (GASummaryDashboard.js) — these two tabs show the
    // same underlying scope, so they need to say so the same way instead of
    // this tab's own separate "YTD26"-style tag.
    const periodIsYtdOnly = period.includes('ytd') && !period.includes('fy');
    const quarterLabel = quarter.join('+');
    const periodLabel = customRange
        ? `${monthAbbrev(customRange.start)}–${monthAbbrev(customRange.end)}`
        : quarter.length
            ? quarterLabel
            : periodIsYtdOnly
                ? `YTD-${monthAbbrev(data.as_of)}`
                : `FY${String(currentYear).slice(-2)}`;
    // Same tag, but for the Prior Year comparison — its figure is already
    // scoped to match (YTD-through-August vs. only YTD-through-August of the
    // prior year, not the whole prior FY), so the label needs to say so too
    // instead of always reading "FY25" even when Period = YTD.
    const priorPeriodLabel = priorYear
        ? (customRange ? `${monthAbbrev(customRange.start)}–${monthAbbrev(customRange.end)} ${priorYear}`
            : quarter.length ? `${quarterLabel} ${priorYear}`
                : periodIsYtdOnly ? `YTD-${monthAbbrev(data.as_of)} ${priorYear}`
                    : `FY${priorYear.slice(2)}`)
        : '';
    // Only the unbounded Full Year view can contain not-yet-actual future
    // months — matches GASummaryDashboard.js's identical isFullYear.
    const isFullYear = !quarter.length && !customRange && !periodIsYtdOnly;

    // The FY Actual/AOP/PY hero tiles and category table narrow to whichever
    // months are in scope (Quarter or Custom Date, summed from each row's
    // monthly series) and to whichever year is active — YTD and Run Rate
    // stay full current-year since those are distinct concepts. The deep-dive
    // modal still shows the complete current year of detail either way.
    const scopeRow = row => ({ ...scopeSummaryRow(row, { quarter, customRange, period, asOf: data.as_of, yearMode, priorYear: rawPriorYear }), ...(!yearMode.includes('current') ? { run_rate: { current: null, dec_aop: null, dec_fcst: null } } : {}) });
    const categoryRows = departmentFilteredRows.map(scopeRow);
    // A category with nothing in any of its $ (or Run Rate) columns for the
    // selected scope is just noise in these tables — e.g. a Country/Status
    // filter that happens to exclude every record a category ever had. Zero
    // is still shown normally if even ONE of its own columns is nonzero
    // (including negative); only an all-zero/all-null row is dropped. Each
    // table below picks whichever of these two matches what it displays.
    const dollarCategoryRows = categoryRows.filter(row => row.fy_actual_forecast || row.fy_aop || row.fy_prior_year);
    const runRateCategoryRows = categoryRows.filter(row => row.run_rate?.current || row.run_rate?.dec_aop || row.run_rate?.dec_fcst);

    // When a Department Sub-Category filter is active, the KPI tiles reflect
    // just those departments' combined row instead of the section-wide
    // total, so the top-of-page numbers always match what's actually shown
    // in the table below. Several selected departments merge into one row
    // first (mergeMonthlyRows), same as summing several Countries/Statuses.
    const total = scopeRow(department.length
        ? (mergeMonthlyRows(departmentFilteredRows) || { label: department.join(' + '), monthly: [], run_rate: { current: 0, dec_aop: 0, dec_fcst: 0 } })
        : sectionData.total);

    // G&A Total spend as a % of Revenue/ARR — company-wide denominator
    // (Revenue/ARR isn't tracked per department), numerator is whichever
    // row (Total, or one category) is being measured, so each row shows
    // its own share of the whole. Null (shown as nothing) when the
    // uploaded workbook had no G&A_Summary sheet with these rows.
    const revenueArr = data.revenue_arr || {};
    const metricOptions = { quarter, customRange, period, asOf: data.as_of, yearMode, priorYear: rawPriorYear };
    const revenueScopedActual = scopedMetricValue(revenueArr.revenue, 'actual', metricOptions);
    const arrScopedActual = scopedMetricValue(revenueArr.ending_arr, 'actual', metricOptions);
    const revenueScopedAop = scopedMetricValue(revenueArr.revenue, 'aop', metricOptions);
    const arrScopedAop = scopedMetricValue(revenueArr.ending_arr, 'aop', metricOptions);
    const revenueScopedPy = scopedMetricValue(revenueArr.revenue, 'py', metricOptions);
    const arrScopedPy = scopedMetricValue(revenueArr.ending_arr, 'py', metricOptions);
    // Each basis divides by the SAME-basis Revenue/ARR — AOP spend vs. AOP
    // Revenue, prior-year spend vs. prior-year Revenue — rather than every
    // column sharing one Actuals-basis denominator, which would mix budget
    // and actual planning bases in one percentage. Var. to AOP is a delta
    // of two already-scoped actuals, so it shares the Actuals-basis
    // denominator (same as the Snapshot section's Variance cards).
    const pctOfRow = (row, field = 'fy_actual_forecast') => {
        const [revenueDenom, arrDenom] = field === 'fy_aop' ? [revenueScopedAop, arrScopedAop]
            : field === 'fy_prior_year' ? [revenueScopedPy, arrScopedPy]
            : [revenueScopedActual, arrScopedActual];
        return {
            revenue: pctOfMetric(row[field], revenueDenom), arr: pctOfMetric(row[field], arrDenom),
            // Raw numerator/denominator behind each % above, for the
            // formula popup's "= $X ÷ $Y × 100 = value" line.
            _raw: { revenue: { num: row[field], denom: revenueDenom }, arr: { num: row[field], denom: arrDenom } },
        };
    };

    // Run Rate is always an annualized, full-year-equivalent figure
    // (run_rates.py never scopes it by quarter/YTD/custom-range) — so its
    // own % of Revenue/ARR must divide by the TRUE full-year Revenue/ARR,
    // not whatever quarter/YTD/custom-range filter is active on the rest of
    // the page (same fix as GASummaryDashboard.js's Run Rate cards).
    const fyMetricOptions = { quarter: '', customRange: null, period: 'fy', asOf: data.as_of, yearMode, priorYear: rawPriorYear };
    const revenueScopedFY = scopedMetricValue(revenueArr.revenue, 'actual', fyMetricOptions);
    const arrScopedFY = scopedMetricValue(revenueArr.ending_arr, 'actual', fyMetricOptions);
    const revenueAopFY = scopedMetricValue(revenueArr.revenue, 'aop', fyMetricOptions);
    const arrAopFY = scopedMetricValue(revenueArr.ending_arr, 'aop', fyMetricOptions);
    // Default field 'current' keeps this call-compatible with
    // RunRateSplitModal's existing pctOfRunRate(r) calls (hc/nonhc split
    // rows only ever have a .current figure). 'dec_aop' is a budget figure
    // so it divides by AOP-basis Revenue/ARR, same pairing as pctOfRow's
    // AOP column; 'dec_fcst' shares Current Run Rate's actual-basis
    // denominator; 'variance' is Dec Fcst Run Rate minus Dec AOP Run Rate
    // (same delta VarianceStack renders) over that same actual-basis figure.
    const pctOfRunRate = (row, field = 'current') => {
        if (field === 'variance') {
            const amount = row?.dec_fcst != null && row?.dec_aop != null ? row.dec_fcst - row.dec_aop : null;
            return {
                revenue: pctOfMetric(amount, revenueScopedFY), arr: pctOfMetric(amount, arrScopedFY),
                _raw: { revenue: { num: amount, denom: revenueScopedFY }, arr: { num: amount, denom: arrScopedFY } },
            };
        }
        const [revenueDenom, arrDenom] = field === 'dec_aop' ? [revenueAopFY, arrAopFY] : [revenueScopedFY, arrScopedFY];
        return {
            revenue: pctOfMetric(row?.[field], revenueDenom), arr: pctOfMetric(row?.[field], arrDenom),
            _raw: { revenue: { num: row?.[field], denom: revenueDenom }, arr: { num: row?.[field], denom: arrDenom } },
        };
    };

    // Formula popups for the By Category / Run Rate by Category % of
    // Revenue/ARR tables below — each cell opens the same PctFormulaModal
    // the Dashboard tab's own % cards use, instead of the $-level category
    // drill `openDrill` below leads to.
    const PCT_ROW_LABELS = { fy_actual_forecast: 'Actuals', fy_aop: 'AOP', fy_prior_year: 'PY', fcst_vs_aop_full_year: 'Var. to AOP' };
    const PCT_ROW_FORMULAS = {
        fy_actual_forecast: metricName => `This category's Actuals+Forecast ÷ ${metricName} for the selected period × 100`,
        fy_aop: metricName => `This category's AOP Budget ÷ AOP ${metricName} for the selected period × 100`,
        fy_prior_year: metricName => `This category's Prior Year Actuals ÷ Prior Year ${metricName} for the selected period × 100`,
        fcst_vs_aop_full_year: metricName => `This category's Var. to AOP ÷ ${metricName} for the selected period × 100`,
    };
    const pctRowCardsFor = (row, metric) => {
        const metricName = metric === 'revenue' ? 'Revenue' : 'Ending ARR';
        return ['fy_actual_forecast', 'fy_aop', 'fy_prior_year', 'fcst_vs_aop_full_year'].map(field => {
            const pct = pctOfRow(row, field);
            const value = pct[metric];
            return {
                label: `${PCT_ROW_LABELS[field]} % of ${metricName}`,
                value: value != null ? `${value.toFixed(1)}%` : '—',
                formula: PCT_ROW_FORMULAS[field](metricName),
                numerator: pct._raw[metric].num,
                denominator: pct._raw[metric].denom,
            };
        });
    };
    const openPctRowDrill = (row, metric) => setPctRowDrill({
        title: `${row.label} — % of ${metric === 'revenue' ? 'Revenue' : 'ARR'} Breakdown`, sub: periodLabel,
        cards: pctRowCardsFor(row, metric),
    });
    const RR_LABELS = { current: 'Current Run Rate', dec_aop: 'Dec AOP Run Rate', dec_fcst: 'Dec FCST Run Rate', variance: 'Var to AOP RR' };
    const RR_FORMULAS = {
        current: metricName => `This category's Current Run Rate ÷ FY ${metricName} × 100`,
        dec_aop: metricName => `This category's Dec AOP Run Rate ÷ FY AOP ${metricName} × 100`,
        dec_fcst: metricName => `This category's Dec FCST Run Rate ÷ FY ${metricName} × 100`,
        variance: metricName => `(This category's Dec FCST Run Rate − Dec AOP Run Rate) ÷ FY ${metricName} × 100`,
    };
    const pctRunRateCardsFor = (row, metric) => {
        const metricName = metric === 'revenue' ? 'Revenue' : 'Ending ARR';
        return ['current', 'dec_aop', 'dec_fcst', 'variance'].map(field => {
            const pct = pctOfRunRate(row?.run_rate, field);
            const value = pct[metric];
            return {
                label: `${RR_LABELS[field]} % of ${metricName}`,
                value: value != null ? `${value.toFixed(1)}%` : '—',
                formula: RR_FORMULAS[field](metricName),
                numerator: pct._raw[metric].num,
                denominator: pct._raw[metric].denom,
            };
        });
    };
    const openPctRunRateDrill = (row, metric) => setPctRowDrill({
        title: `${row.label} — Run Rate % of ${metric === 'revenue' ? 'Revenue' : 'ARR'} Breakdown`, sub: 'Annualized',
        cards: pctRunRateCardsFor(row, metric),
    });

    const openDrill = (row, pctMetric = null) => setDrill([{ type: 'category', row, sectionKey: section, pctMetric }]);
    const topDrill = drill && drill.length ? drill[drill.length - 1] : null;

    // The Headcount/Non-Headcount row for one category label (or the
    // section-wide Total) — ga_summary.py's "Total" row isn't itself keyed
    // into by_category, so that one case reads .total directly instead.
    const sourceRowFor = (sectionObj, label) => (!sectionObj ? null : (label === 'Total' ? sectionObj.total : sectionObj.by_category?.[label]));

    // Employee/Non-HC records are fetched once each and cached — both raw
    // endpoints return every current record regardless of category or
    // month, so a single fetch per source covers every drill.
    const ensureRecordsLoaded = source => {
        if (source === 'total') { ensureRecordsLoaded('hc'); ensureRecordsLoaded('nonhc'); return; }
        if (source === 'hc') {
            if (hcRecords || hcLoading) return;
            setHcLoading(true); setHcError('');
            fetch(`${API_URL}/api/ga/raw/`, { credentials: 'include' })
                .then(res => readApiJson(res, 'Could not load employee records.'))
                .then(body => setHcRecords(body.records || []))
                .catch(err => setHcError(err.message))
                .finally(() => setHcLoading(false));
        } else {
            if (nonhcRecords || nonhcLoading) return;
            setNonhcLoading(true); setNonhcError('');
            fetch(`${API_URL}/api/ga/nonhc/raw/`, { credentials: 'include' })
                .then(res => readApiJson(res, 'Could not load Non-HC records.'))
                .then(body => setNonhcRecords(body.records || []))
                .catch(err => setNonhcError(err.message))
                .finally(() => setNonhcLoading(false));
        }
    };

    const drillRecords = (source, kind, months, monthLabel, cat) => {
        setDrill(stack => [...stack, { type: 'records', source, kind, months, monthLabel, category: cat }]);
        ensureRecordsLoaded(source);
    };

    // HC/Non-HC split for one category (or Total), summed over `months` —
    // `months` is a single month for a Monthly Detail cell, or every month
    // in the row for an FY Total drill, so this one function covers both.
    const pushMonthSplit = (label, months, monthLabel) => {
        const hcRow = sourceRowFor(data.headcount_costs, label);
        const nonhcRow = sourceRowFor(data.non_headcount_costs, label);
        const sumOver = (row, key) => {
            const monthRows = (row?.monthly || []).filter(m => months.includes(m.month));
            const modes = yearMode.length ? yearMode : ['current'];
            return modes.reduce((total, ym) => total + monthRows.reduce((s, m) => s + (ym === 'prior' ? (key === 'actual' ? m.py || 0 : 0) : m[key] || 0), 0), 0);
        };
        setDrill(stack => [...stack, {
            type: 'month', category: label, months, monthLabel,
            hc: { actual: sumOver(hcRow, 'actual'), aop: sumOver(hcRow, 'aop'), py: sumOver(hcRow, 'py') },
            nonhc: { actual: sumOver(nonhcRow, 'actual'), aop: sumOver(nonhcRow, 'aop'), py: sumOver(nonhcRow, 'py') },
        }]);
    };

    // Which month counts as "the last actual month" / "December" for Run
    // Rate, for one source's own monthly series — mirrors
    // GASummaryDashboard.js's categoryRunRateSplit, since every
    // backend row already carries its own run_rate but not the month keys
    // an individual record's Run Rate needs to be recomputed from.
    const runRateMonths = monthly => {
        if (!monthly?.length) return { lastActualMonth: null, decMonth: null };
        const asOf = data.as_of;
        const ytdLen = asOf ? monthly.filter(m => m.month <= asOf).length : monthly.length;
        return {
            lastActualMonth: monthly[Math.max(ytdLen - 1, 0)]?.month,
            decMonth: `${monthly[0].month.slice(0, 4)}-12`,
        };
    };

    // `fresh` starts a brand-new drill stack (used by the standalone "Run
    // Rate by Category" section below, opened directly from the page rather
    // than from inside an already-open category drill) instead of pushing
    // onto whatever's currently open.
    const pushRunRateRecords = (source, label, monthly, fresh = false) => {
        const { lastActualMonth, decMonth } = runRateMonths(monthly);
        const layer = { type: 'raterun-records', source, category: label, lastActualMonth, decMonth };
        setDrill(stack => (fresh ? [layer] : [...stack, layer]));
        ensureRecordsLoaded(source);
    };

    const openRunRateSplitFor = (label, sectionKey, fresh = false, pctMetric = null) => {
        if (!yearMode.includes('current')) return;
        const hcRow = sourceRowFor(data.headcount_costs, label);
        const nonhcRow = sourceRowFor(data.non_headcount_costs, label);
        if (sectionKey === 'ga_total') {
            const layer = {
                type: 'raterun', label, pctMetric,
                hc: hcRow?.run_rate || { current: 0, dec_aop: 0, dec_fcst: 0 },
                nonhc: nonhcRow?.run_rate || { current: 0, dec_aop: 0, dec_fcst: 0 },
                hcMonthly: hcRow?.monthly, nonhcMonthly: nonhcRow?.monthly,
            };
            setDrill(stack => (fresh ? [layer] : [...stack, layer]));
        } else {
            const isHc = sectionKey === 'headcount_costs';
            pushRunRateRecords(isHc ? 'hc' : 'nonhc', label, isHc ? hcRow?.monthly : nonhcRow?.monthly, fresh);
        }
    };
    const openRunRateSplit = () => openRunRateSplitFor(topDrill.row.label, topDrill.sectionKey);

    return (
        <div className="legal-ov">
            {topDrill?.type === 'category' && (
                <CategoryDrillModal
                    row={topDrill.row} currentYear={currentYear} priorYear={priorYear} sectionKey={topDrill.sectionKey}
                    pctActual={pctOfRow(topDrill.row)} pctAop={pctOfRow(topDrill.row, 'fy_aop')}
                    pctMetric={topDrill.pctMetric} pctOfRow={pctOfRow} pctOfRunRate={pctOfRunRate} revenueArr={revenueArr}
                    onClose={() => setDrill(null)}
                    onDrillMonth={(month, monthLabel) => pushMonthSplit(topDrill.row.label, [month], monthLabel)}
                    onDrillRecords={(kind, months, monthLabel) => drillRecords(
                        topDrill.sectionKey === 'headcount_costs' ? 'hc' : 'nonhc', kind, months, monthLabel, topDrill.row.label,
                    )}
                    onFyTotalClick={kind => {
                        const months = topDrill.row.monthly.map(m => m.month);
                        if (topDrill.sectionKey === 'ga_total') pushMonthSplit(topDrill.row.label, months, `${periodLabel} Total`);
                        else drillRecords(topDrill.sectionKey === 'headcount_costs' ? 'hc' : 'nonhc', kind, months, `${periodLabel} Total`, topDrill.row.label);
                    }}
                    onRunRateClick={openRunRateSplit}
                />
            )}
            {topDrill?.type === 'month' && (
                <MonthSplitModal
                    title={`${topDrill.category} — ${topDrill.monthLabel}`}
                    sub="Headcount vs. Non-Headcount split · click a cell for its records"
                    hc={topDrill.hc} nonhc={topDrill.nonhc}
                    onClose={() => setDrill(null)}
                    onBack={() => setDrill(stack => stack.slice(0, -1))}
                    onDrillCell={(source, kind) => drillRecords(source, kind, topDrill.months, topDrill.monthLabel, topDrill.category)}
                />
            )}
            {topDrill?.type === 'raterun' && (
                <RunRateSplitModal
                    label={topDrill.label} hc={topDrill.hc} nonhc={topDrill.nonhc}
                    pctOfRunRate={pctOfRunRate} pctMetric={topDrill.pctMetric}
                    onClose={() => setDrill(null)}
                    onBack={() => setDrill(stack => stack.slice(0, -1))}
                    onDrillSource={source => pushRunRateRecords(source, topDrill.label, source === 'hc' ? topDrill.hcMonthly : topDrill.nonhcMonthly)}
                />
            )}
            {topDrill?.type === 'raterun-records' && (
                <RunRateRecordsModal
                    source={topDrill.source} category={topDrill.category}
                    lastActualMonth={topDrill.lastActualMonth} decMonth={topDrill.decMonth}
                    currentYear={currentYear}
                    records={recordsFor(topDrill.source).filter(r => topDrill.category === 'Total' || (r.category || 'Unspecified') === topDrill.category)}
                    loading={topDrill.source === 'total' ? hcLoading || nonhcLoading : topDrill.source === 'hc' ? hcLoading : nonhcLoading}
                    error={topDrill.source === 'total' ? hcError || nonhcError : topDrill.source === 'hc' ? hcError : nonhcError}
                    onClose={() => setDrill(null)}
                    onBack={() => setDrill(stack => stack.slice(0, -1))}
                />
            )}
            {topDrill?.type === 'records' && (
                <RecordsListModal
                    source={topDrill.source} kind={topDrill.kind} months={topDrill.months} monthLabel={topDrill.monthLabel}
                    category={topDrill.category} currentYear={currentYear} priorYear={priorYear}
                    records={recordsFor(topDrill.source).filter(r => topDrill.category === 'Total' || (r.category || 'Unspecified') === topDrill.category)}
                    loading={topDrill.source === 'total' ? hcLoading || nonhcLoading : topDrill.source === 'hc' ? hcLoading : nonhcLoading}
                    error={topDrill.source === 'total' ? hcError || nonhcError : topDrill.source === 'hc' ? hcError : nonhcError}
                    onClose={() => setDrill(null)}
                    onBack={() => setDrill(stack => stack.slice(0, -1))}
                />
            )}
            {pctFormulaDrill && <PctFormulaModal {...pctFormulaDrill} onClose={() => setPctFormulaDrill(null)} />}
            {pctRowDrill && <PctRowDrillModal {...pctRowDrill} onClose={() => setPctRowDrill(null)} />}

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{SECTION_LABEL[section]}</span>
                {(department.length || quarter.length || customRange || !(yearMode.length === 1 && yearMode[0] === 'current')) && (
                    <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#65676b', fontWeight: 600 }}>
                        Showing: {[department.join(' + '), periodLabel, isPriorOnlyView ? 'Prior Year view' : yearMode.includes('prior') ? 'Current + Prior Year blended' : ''].filter(Boolean).join(' · ')}
                    </span>
                )}
            </div>

            <div className="esd-kpi-grid" style={{ marginBottom: 18 }}>
                <KpiTile
                    tone="a" icon="💵" label={`${periodLabel} Actuals${isFullYear ? ' + Forecast' : ''}`} value={fmtMoney(total.fy_actual_forecast)}
                    sub={pctLine(pctOfRow(total))}
                    onClick={() => openDrill(total)}
                />
                <KpiTile
                    tone="b" icon="🎯" label={`${periodLabel} AOP`} value={fmtMoney(total.fy_aop)}
                    sub={pctLine(pctOfRow(total, 'fy_aop'))}
                    onClick={() => openDrill(total)}
                />
                <KpiTile
                    tone="c" icon="📅" label={priorPeriodLabel || 'Prior Year'}
                    value={priorYear ? fmtMoney(total.fy_prior_year) : 'N/A'}
                    onClick={() => openDrill(total)}
                />
                <KpiTile
                    tone={total.fcst_vs_aop_full_year <= 0 ? 'good' : 'bad'} icon={total.fcst_vs_aop_full_year <= 0 ? '✅' : '⚠️'}
                    label={`${periodLabel} vs. AOP`} value={fmtMoney(total.fcst_vs_aop_full_year)}
                    valueTone={total.fcst_vs_aop_full_year <= 0 ? 'good' : 'bad'}
                    sub={variancePct(total.fy_actual_forecast, total.fy_aop)} subTone={total.fcst_vs_aop_full_year <= 0 ? 'good' : 'bad'}
                    onClick={() => openDrill(total)}
                />
                <KpiTile
                    tone={total.fcst_vs_py_full_year <= 0 ? 'good' : 'bad'} icon={total.fcst_vs_py_full_year <= 0 ? '✅' : '⚠️'}
                    label={`${periodLabel} vs. PY`} value={fmtMoney(total.fcst_vs_py_full_year)}
                    valueTone={total.fcst_vs_py_full_year <= 0 ? 'good' : 'bad'}
                    sub={variancePct(total.fy_actual_forecast, total.fy_prior_year)} subTone={total.fcst_vs_py_full_year <= 0 ? 'good' : 'bad'}
                    onClick={() => openDrill(total)}
                />
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">🏷️ By Category</div>
                <div className="legal-ic-sub">Click a row for the full monthly / quarterly breakdown</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Category</th>
                                <th className="bm-th">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''}</th>
                                <th className="bm-th">{periodLabel} AOP</th>
                                <th className="bm-th">{priorYear ? `${priorPeriodLabel} PY` : 'PY (N/A)'}</th>
                                <th className="bm-th">Var. to AOP</th>
                                <th className="bm-th">Var. to PY</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dollarCategoryRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openDrill(row)}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{fmtMoney(row.fy_actual_forecast)}</td>
                                    <td className="bm-td-r">{fmtMoney(row.fy_aop)}</td>
                                    <td className="bm-td-r">{fmtMoney(row.fy_prior_year)}</td>
                                    <td className="bm-td-r"><VarianceStack actual={row.fy_actual_forecast} base={row.fy_aop} /></td>
                                    <td className="bm-td-r"><VarianceStack actual={row.fy_actual_forecast} base={row.fy_prior_year} /></td>
                                </tr>
                            ))}
                            {!dollarCategoryRows.length && (
                                <tr><td colSpan={6} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr
                                className="bm-row"
                                style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }}
                                onClick={() => openDrill(total)}
                                title="View monthly / quarterly breakdown for the total"
                            >
                                <td>Total</td>
                                <td className="bm-td-r">{fmtMoney(total.fy_actual_forecast)}</td>
                                <td className="bm-td-r">{fmtMoney(total.fy_aop)}</td>
                                <td className="bm-td-r">{fmtMoney(total.fy_prior_year)}</td>
                                <td className="bm-td-r"><VarianceStack actual={total.fy_actual_forecast} base={total.fy_aop} /></td>
                                <td className="bm-td-r"><VarianceStack actual={total.fy_actual_forecast} base={total.fy_prior_year} /></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Pulled out of the By Category table's own % of Revenue / %
                of ARR columns above into their own dedicated tables — same
                per-row values (Actuals-basis), just presented on their own
                instead of two more columns squeezed onto an already-wide
                row. */}
            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">💹 By Category — % of Revenue</div>
                <div className="legal-ic-sub">Each basis (Actuals, AOP, PY, Var. to AOP) as a share of its own matching-basis Revenue</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Category</th>
                                <th className="bm-th">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''} % of Revenue</th>
                                <th className="bm-th">{periodLabel} AOP % of Revenue</th>
                                <th className="bm-th">{priorYear ? `${priorPeriodLabel} PY` : 'PY (N/A)'} % of Revenue</th>
                                <th className="bm-th">Var. to AOP % of Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dollarCategoryRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openPctRowDrill(row, 'revenue')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_actual_forecast').revenue != null ? `${pctOfRow(row, 'fy_actual_forecast').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_aop').revenue != null ? `${pctOfRow(row, 'fy_aop').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_prior_year').revenue != null ? `${pctOfRow(row, 'fy_prior_year').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fcst_vs_aop_full_year').revenue != null ? `${pctOfRow(row, 'fcst_vs_aop_full_year').revenue.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!dollarCategoryRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openPctRowDrill({ ...total, label: 'Total' }, 'revenue')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_actual_forecast').revenue != null ? `${pctOfRow(total, 'fy_actual_forecast').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_aop').revenue != null ? `${pctOfRow(total, 'fy_aop').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_prior_year').revenue != null ? `${pctOfRow(total, 'fy_prior_year').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fcst_vs_aop_full_year').revenue != null ? `${pctOfRow(total, 'fcst_vs_aop_full_year').revenue.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">📡 By Category — % of ARR</div>
                <div className="legal-ic-sub">Each basis (Actuals, AOP, PY, Var. to AOP) as a share of its own matching-basis Ending ARR</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Category</th>
                                <th className="bm-th">{periodLabel} Actuals{isFullYear ? ' + Forecast' : ''} % of ARR</th>
                                <th className="bm-th">{periodLabel} AOP % of ARR</th>
                                <th className="bm-th">{priorYear ? `${priorPeriodLabel} PY` : 'PY (N/A)'} % of ARR</th>
                                <th className="bm-th">Var. to AOP % of ARR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dollarCategoryRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openPctRowDrill(row, 'arr')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_actual_forecast').arr != null ? `${pctOfRow(row, 'fy_actual_forecast').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_aop').arr != null ? `${pctOfRow(row, 'fy_aop').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fy_prior_year').arr != null ? `${pctOfRow(row, 'fy_prior_year').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRow(row, 'fcst_vs_aop_full_year').arr != null ? `${pctOfRow(row, 'fcst_vs_aop_full_year').arr.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!dollarCategoryRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openPctRowDrill({ ...total, label: 'Total' }, 'arr')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_actual_forecast').arr != null ? `${pctOfRow(total, 'fy_actual_forecast').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_aop').arr != null ? `${pctOfRow(total, 'fy_aop').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fy_prior_year').arr != null ? `${pctOfRow(total, 'fy_prior_year').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRow(total, 'fcst_vs_aop_full_year').arr != null ? `${pctOfRow(total, 'fcst_vs_aop_full_year').arr.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">🔁 Run Rate by Category</div>
                <div className="legal-ic-sub">Click a row for the Headcount / Non-Headcount split{department.length ? '' : ' — click Total for the company-wide split'}</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Category</th>
                                <th className="bm-th">Current Run Rate</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} AOP Run Rate</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} FCST Run Rate</th>
                                <th className="bm-th">Var to AOP RR and %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runRateCategoryRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openRunRateSplitFor(row.label, section, true)}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{fmtMoney(row.run_rate?.current)}</td>
                                    <td className="bm-td-r">{fmtMoney(row.run_rate?.dec_aop)}</td>
                                    <td className="bm-td-r">{fmtMoney(row.run_rate?.dec_fcst)}</td>
                                    <td className="bm-td-r"><VarianceStack actual={row.run_rate?.dec_fcst} base={row.run_rate?.dec_aop} /></td>
                                </tr>
                            ))}
                            {!runRateCategoryRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr
                                className="bm-row"
                                style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }}
                                onClick={() => openRunRateSplitFor(total.label, section, true)}
                                title="View Run Rate split for the total"
                            >
                                <td>Total</td>
                                <td className="bm-td-r">{fmtMoney(total.run_rate?.current)}</td>
                                <td className="bm-td-r">{fmtMoney(total.run_rate?.dec_aop)}</td>
                                <td className="bm-td-r">{fmtMoney(total.run_rate?.dec_fcst)}</td>
                                <td className="bm-td-r"><VarianceStack actual={total.run_rate?.dec_fcst} base={total.run_rate?.dec_aop} /></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Same pull-out as the By Category tables above, but for
                Current Run Rate's % of Revenue / % of ARR. */}
            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">💹 Run Rate by Category — % of Revenue</div>
                <div className="legal-ic-sub">Each Run Rate basis as a share of its own matching-basis Revenue</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Category</th>
                                <th className="bm-th">Current Run Rate % of Revenue</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} AOP Run Rate % of Revenue</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} FCST Run Rate % of Revenue</th>
                                <th className="bm-th">Var to AOP RR % of Revenue</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runRateCategoryRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openPctRunRateDrill(row, 'revenue')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'current').revenue != null ? `${pctOfRunRate(row.run_rate, 'current').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_aop').revenue != null ? `${pctOfRunRate(row.run_rate, 'dec_aop').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_fcst').revenue != null ? `${pctOfRunRate(row.run_rate, 'dec_fcst').revenue.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'variance').revenue != null ? `${pctOfRunRate(row.run_rate, 'variance').revenue.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!runRateCategoryRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openPctRunRateDrill({ ...total, label: 'Total' }, 'revenue')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'current').revenue != null ? `${pctOfRunRate(total.run_rate, 'current').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_aop').revenue != null ? `${pctOfRunRate(total.run_rate, 'dec_aop').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_fcst').revenue != null ? `${pctOfRunRate(total.run_rate, 'dec_fcst').revenue.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'variance').revenue != null ? `${pctOfRunRate(total.run_rate, 'variance').revenue.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div className="legal-ic" style={{ marginTop: 18 }}>
                <div className="legal-ic-title">📡 Run Rate by Category — % of ARR</div>
                <div className="legal-ic-sub">Each Run Rate basis as a share of its own matching-basis Ending ARR</div>
                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th">Category</th>
                                <th className="bm-th">Current Run Rate % of ARR</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} AOP Run Rate % of ARR</th>
                                <th className="bm-th">Dec{currentYear ? ` '${currentYear.slice(2)}` : ''} FCST Run Rate % of ARR</th>
                                <th className="bm-th">Var to AOP RR % of ARR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runRateCategoryRows.map(row => (
                                <tr className="bm-row" key={row.label} style={{ cursor: 'pointer' }} onClick={() => openPctRunRateDrill(row, 'arr')}>
                                    <td className="bm-td-primary">{row.label}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'current').arr != null ? `${pctOfRunRate(row.run_rate, 'current').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_aop').arr != null ? `${pctOfRunRate(row.run_rate, 'dec_aop').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'dec_fcst').arr != null ? `${pctOfRunRate(row.run_rate, 'dec_fcst').arr.toFixed(1)}%` : '—'}</td>
                                    <td className="bm-td-r">{pctOfRunRate(row.run_rate, 'variance').arr != null ? `${pctOfRunRate(row.run_rate, 'variance').arr.toFixed(1)}%` : '—'}</td>
                                </tr>
                            ))}
                            {!runRateCategoryRows.length && (
                                <tr><td colSpan={5} className="bm-empty-row">No categories found.</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f8fafd', fontWeight: 700, cursor: 'pointer' }} onClick={() => openPctRunRateDrill({ ...total, label: 'Total' }, 'arr')}>
                                <td>Total</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'current').arr != null ? `${pctOfRunRate(total.run_rate, 'current').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_aop').arr != null ? `${pctOfRunRate(total.run_rate, 'dec_aop').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'dec_fcst').arr != null ? `${pctOfRunRate(total.run_rate, 'dec_fcst').arr.toFixed(1)}%` : '—'}</td>
                                <td className="bm-td-r">{pctOfRunRate(total.run_rate, 'variance').arr != null ? `${pctOfRunRate(total.run_rate, 'variance').arr.toFixed(1)}%` : '—'}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
}

export { GASummary };
export default GASummary;
